import { Injectable } from '@angular/core';
import { AppClientDataService } from '../../shared/services/client-data-service/app-client-data.service';
import { BaseService } from '../../shared/base-classes/base.service';
import {
  BehaviorCodeEntryCreateDto,
  BehaviorCodeEntryResponseDto,
  BehaviorCodeEntryUpdateDto,
  BehaviorCodeEntryUpdateNotesDto,
  BehaviorCodeResponseDto,
  IDisplayData,
  PaginationResultsDto,
  ReactionEntriesListResponseDto,
  ReactionEntriesUpdateDto,
  RelationDto,
  UploadBehaviorOperationModeType,
  BooleanStringTransform
} from '@whetstoneeducation/hero-common';
import { IBehaviorCodeEntryConfirmReactionsResolverData } from './behavior-code-entry-confirm-reactions/behavior-code-entry-confirm-reaction-models/behavior-code-entry-confirm-reactions-resolver-data.interface';
import { TableFilterOptions } from '../../shared/tables/table-filters/table-filters';
import { TableFiltersPageKeyEnum } from '../../shared/tables/table-filters/table-filters-page-key.enum';
import { ITableFilters } from '../../shared/tables/table-filters/table-filters.interface';
import { AppBehaviorCodesService } from '../behavior-codes/behavior-codes.service';
import { HttpClient } from '@angular/common/http';
import { EnvService } from '../../env.service';
import { logger } from '../../shared/logger';

@Injectable({
  providedIn: 'root'
})
export class AppBehaviorCodeEntryService extends BaseService {
  private readonly API_GATEWAY_URL: string;
  constructor(
    public clientDataService: AppClientDataService,
    public behaviorCodeService: AppBehaviorCodesService,
    public http: HttpClient,
    private envService: EnvService
  ) {
    super();
    this.API_GATEWAY_URL = this.envService.apiGatewayUrl;
  }

  public async getBehaviorCodeOptions(
    schoolId: number
  ): Promise<IDisplayData[]> {
    return this.clientDataService.execute<IDisplayData[]>(
      this.GET_ROUTES.BEHAVIOR_CODE_DROPDOWN,
      {
        method: this.METHOD.GET,
        pathParams: {
          schoolId
        }
      }
    );
  }

  public async getBehaviorCodeTypesWithBehaviorCodes(
    schoolGroupId?: number
  ): Promise<RelationDto[]> {
    return this.clientDataService.execute<RelationDto[]>(
      this.GET_ROUTES.BEHAVIOR_CODE_TYPES_LIST_WITH_CODES,
      {
        queryParams: {
          schoolGroupId: schoolGroupId
        }
      }
    );
  }

  public async getBehaviorCodesByType(
    behaviorCodeTypeId: number
  ): Promise<RelationDto[]> {
    return this.clientDataService.execute<RelationDto[]>(
      this.GET_ROUTES.BEHAVIOR_CODES_LIST_BY_BEHAVIOR_CODE_TYPE,
      {
        pathParams: {
          behaviorCodeTypeId
        }
      }
    );
  }

  public async getReactionOptions(
    behaviorCodeId: number,
    studentId: number,
    reactionType?: string,
  ): Promise<RelationDto[]> {
    const queryParams: {
      behaviorCodeId: number,
      studentId: number,
      reactionType?: string,
    } = {
      behaviorCodeId,
      studentId,
    };

    if (reactionType) {
      queryParams.reactionType = reactionType;
    }

    return this.clientDataService.execute<RelationDto[]>(
      this.GET_ROUTES.BEHAVIOR_CODE_NEXT_POSSIBLE_REACTION_OPTIONS,
      { queryParams },
    );
  }

  public async getScheduledReactionsOptions(
    behaviorCodeReactionId: number,
    studentId: number
  ): Promise<RelationDto[]> {
    return this.clientDataService.execute<RelationDto[]>(
      this.GET_ROUTES.BEHAVIOR_CODE_REACTION_SCHEDULED_REACTION_OPTIONS,
      {
        queryParams: {
          behaviorCodeReactionId,
          studentId
        }
      }
    );
  }

  public async uploadBehaviorCodeEntries(
    file: File,
    operationMode: UploadBehaviorOperationModeType,
    behaviorCodeId: number,
    notes?: string
  ) {
    const formData = new FormData();
    formData.append('file', file, file.name);
    let urlPart = `?behaviorCodeId=${behaviorCodeId}`;
    urlPart = notes ? `${urlPart}&notes=${notes}` : urlPart;
    return this.http.post(
      this.clientDataService.setUrlPathParameters(
        this.API_GATEWAY_URL +
          this.POST_ROUTES.UPLOAD_BEHAVIOR_CODE_ENTRIES +
          urlPart,
        { operationMode: operationMode }
      ),
      formData,
      { headers: { Version: '1' }, withCredentials: true }
    );
  }

  public async batchCreateBehaviorCodeEntries(
    behaviorCodeEntryCreateDtos: BehaviorCodeEntryCreateDto[]
  ): Promise<BehaviorCodeEntryResponseDto[]> {
    return this.clientDataService.execute<BehaviorCodeEntryResponseDto[]>(
      this.POST_ROUTES.BATCH_CREATE_BEHAVIOR_CODE_ENTRIES,
      {
        method: this.METHOD.POST,
        body: behaviorCodeEntryCreateDtos
      }
    );
  }

  public async generatePrintData(
    printerType: string,
    reactionEntry: ReactionEntriesListResponseDto
  ): Promise<string | Uint8Array> {

    if (printerType === 'esc-pos' || printerType === 'star-prnt') {
      return this.clientDataService.execute<Uint8Array>(
        this.POST_ROUTES.PRINT_TRACK,
        {
          method: this.METHOD.POST,
          body: {
            printerType,
            data: reactionEntry
          },
          responseType: 'arraybuffer'
        }
      );
    } 
    
    return this.clientDataService.execute<string>(
      this.POST_ROUTES.PRINT_TRACK,
      {
        method: this.METHOD.POST,
        body: {
          printerType,
          data: reactionEntry
        },
        responseType: 'text'
      }
    );
  }

  public async getBehaviorCodeEntryConfirmReactionsResolverData(
    behaviorCodeEntriesIds: number[],
    schoolId: number,
    addPrintInfo?: boolean
  ): Promise<IBehaviorCodeEntryConfirmReactionsResolverData> {
    const tableFilters = TableFilterOptions.getPageDefault(
      TableFiltersPageKeyEnum.COMPLIANCE_LIST
    );
    const reactionEntries = await this.getBehaviorCodeEntriesByIds(
      behaviorCodeEntriesIds,
      schoolId,
      tableFilters,
      addPrintInfo
    );

    // cache behavior codes so we don't have to look them up for each reaction entry
    // if we already have the behavior code, we don't need to look it up again
    // Currently we will only see one behavior code for this list of reaction entries
    // but this code will support multiple if needed
    const behaviorCodes = new Map<number, BehaviorCodeResponseDto>();
    for (const reactionEntry of reactionEntries.results) {
      if (!behaviorCodes.has(reactionEntry.behaviorCodeId)) {
        try {
          const behaviorCode = await this.behaviorCodeService.getBehaviorCodeById(
            reactionEntry.behaviorCodeId
          );
          behaviorCodes.set(reactionEntry.behaviorCodeId, behaviorCode);
        } catch (err) {
          // Print uses printPass on the reaction row when this lookup is forbidden.
          logger.error(err);
        }
      }
    }

    return {
      reactionEntries,
      tableFilters,
      behaviorCodes,
      behaviorCodeEntriesIds
    };
  }

  public printsPass(
    reactionEntry: ReactionEntriesListResponseDto,
    behaviorCode?: Pick<BehaviorCodeResponseDto, 'printPass'> | null
  ): boolean {
    const printPass = (
      reactionEntry as ReactionEntriesListResponseDto & {
        printPass?: boolean | number;
      }
    ).printPass;
    const flag = printPass ?? behaviorCode?.printPass;
    return BooleanStringTransform({ value: flag } as any) === true;
  }

  public async getBehaviorCodeEntriesByIds(
    ids: number[],
    schoolId: number,
    tableFilters: ITableFilters,
    addPrintInfo?: boolean
  ): Promise<PaginationResultsDto<ReactionEntriesListResponseDto>> {
    return this.clientDataService.execute<
      PaginationResultsDto<ReactionEntriesListResponseDto>
    >(this.GET_ROUTES.REACTION_ENTRIES_BY_BEHAVIOR_CODE_ENTRY_IDS, {
      queryParams: {
        ids,
        schoolId,
        addPrintInfo,
        ...tableFilters
      }
    });
  }

  public async updateReactionEntry(
    id: number,
    dto: ReactionEntriesUpdateDto
  ): Promise<ReactionEntriesListResponseDto> {
    return this.clientDataService.execute<ReactionEntriesListResponseDto>(
      this.PUT_ROUTES.UPDATE_REACTION_ENTRY,
      {
        method: this.METHOD.PUT,
        pathParams: {
          id
        },
        body: dto
      }
    );
  }

  public async getBehaviorCodeEntry(
    id: number
  ): Promise<BehaviorCodeEntryResponseDto> {
    return this.clientDataService.execute<BehaviorCodeEntryResponseDto>(
      this.GET_ROUTES.BEHAVIOR_CODE_ENTRY_BY_ID,
      {
        method: this.METHOD.GET,
        pathParams: {
          id
        }
      }
    );
  }

  public async deleteBehaviorCodeEntry(
    id: number
  ): Promise<BehaviorCodeEntryResponseDto> {
    const schoolId = this.StorageManager.getLoggedInUser().currentSchoolId;
    if (!schoolId) {
      throw new Error('User does not have a current school.');
    }
    return this.clientDataService.execute<BehaviorCodeEntryResponseDto>(
      this.DELETE_ROUTES.DELETE_BEHAVIOR_CODE_ENTRY,
      {
        method: this.METHOD.DELETE,
        pathParams: {
          id
        },
        queryParams: {
          schoolId
        }
      }
    );
  }

  public async updateBehaviorCodeEntry(
    updateDto: BehaviorCodeEntryUpdateDto,
    id: number
  ): Promise<BehaviorCodeEntryResponseDto> {
    return this.clientDataService.execute<BehaviorCodeEntryResponseDto>(
      this.PUT_ROUTES.UPDATE_BEHAVIOR_CODE_ENTRY,
      {
        method: this.METHOD.PUT,
        pathParams: { id },
        body: updateDto
      }
    );
  }

  public async updateBehaviorCodeEntryNotes(
    updateDto: BehaviorCodeEntryUpdateNotesDto,
    id: number
  ): Promise<BehaviorCodeEntryResponseDto> {
    return this.clientDataService.execute<BehaviorCodeEntryResponseDto>(
      this.PUT_ROUTES.UPDATE_BEHAVIOR_CODE_ENTRY_NOTES,
      {
        method: this.METHOD.PUT,
        pathParams: { id },
        body: updateDto
      }
    );
  }

  public async getFastTrackBehaviorCodes(): Promise<BehaviorCodeResponseDto[]> {
    return this.clientDataService.execute<BehaviorCodeResponseDto[]>(
      this.GET_ROUTES.BEHAVIOR_CODES_FAST_TRACK
    );
  }
}
